import type { TimelineItem } from "@travel/shared";

// Extract a Maps-friendly place query from an item. Prefers the googleMapLink
// (q= param, /place/<name>, or /@lat,lng coordinates) and falls back to title.
export function getPlaceQuery(item: Pick<TimelineItem, "title" | "googleMapLink">): string | null {
  if (item.googleMapLink) {
    try {
      const url = new URL(item.googleMapLink);
      const q = url.searchParams.get("q");
      if (q && q.trim()) return q.trim();

      const placeMatch = url.pathname.match(/\/place\/([^/]+)/);
      if (placeMatch?.[1]) {
        const decoded = decodeURIComponent(placeMatch[1]).replace(/\+/g, " ").trim();
        if (decoded) return decoded;
      }

      const atMatch = url.pathname.match(/\/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (atMatch) return `${atMatch[1]},${atMatch[2]}`;
    } catch {
      // not a parseable URL — fall through to title
    }
  }
  return item.title?.trim() || null;
}

// Builds a Google Maps Directions URL chaining places in order.
// Returns null if fewer than 2 places (need at least origin + destination).
export function buildDirectionsUrl(places: string[]): string | null {
  if (places.length < 2) return null;
  const params = new URLSearchParams();
  params.set("api", "1");
  params.set("origin", places[0]!);
  params.set("destination", places[places.length - 1]!);
  if (places.length > 2) {
    params.set("waypoints", places.slice(1, -1).join("|"));
  }
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}
